import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../components';
import ComplianceNotice from '../components/ComplianceNotice';
import './RetirementReadinessCenter.css';

const COPY = {
  en: {
    title: 'Retirement Planning Helper',
    subtitle:
      'Answer practical retirement questions in order. Then review exactly what nest egg and monthly savings target your plan implies.',
    labels: {
      profileTitle: 'Step 1: Retirement profile',
      profileSubtitle: 'Set your timing and retirement income target in today dollars.',
      currentAge: 'Current age',
      currentAgeHelp: 'Your age now.',
      retirementAge: 'Retirement age',
      retirementAgeHelp: 'Age when you plan to stop full-time work.',
      planEndAge: 'Plan end age',
      planEndAgeHelp: 'Age up to which your retirement income plan should hold.',
      monthlyNeed: 'Target monthly retirement income (today dollars)',
      monthlyNeedHelp: 'Total monthly amount you want available in retirement before splitting by sources.',
      monthlyContribution: 'Current monthly retirement contribution',
      monthlyContributionHelp: 'What you currently save each month toward retirement.',

      benefitsTitle: 'Step 2: Government benefits (CPP, OAS, GIS)',
      benefitsSubtitle: 'Include only the monthly amounts you expect to receive at retirement.',
      includeBenefits: 'Include CPP/OAS/GIS in retirement income plan',
      cpp: 'CPP monthly amount',
      cppHelp: 'CPP = Canada Pension Plan. Find estimate in My Service Canada Account.',
      oas: 'OAS monthly amount',
      oasHelp: 'OAS = Old Age Security. Amount depends on age and residency history.',
      gis: 'GIS monthly amount',
      gisHelp: 'GIS = Guaranteed Income Supplement for eligible low-income OAS recipients.',
      cppLinkLabel: 'CPP estimate and statement',
      oasLinkLabel: 'OAS program overview',
      gisLinkLabel: 'GIS program overview',

      assetsTitle: 'Step 3: Retirement assets today',
      assetsSubtitle: 'Balances available for long-term retirement income planning.',
      rrsp: 'RRSP balance',
      rrspHelp: 'Total RRSP value today.',
      tfsa: 'TFSA balance',
      tfsaHelp: 'Total TFSA value available for retirement.',
      nonReg: 'Non-registered balance',
      nonRegHelp: 'Taxable account value available for retirement.',

      assumptionsTitle: 'Step 4: Return and withdrawal settings',
      assumptionsSubtitle: 'Choose how your portfolio should behave before and during retirement.',
      annualReturnPre: 'Annual return before retirement (%)',
      annualReturnPreHelp: 'Expected average yearly growth while still contributing.',
      annualReturnPost: 'Annual return during retirement (%)',
      annualReturnPostHelp: 'Expected average yearly growth while withdrawing income.',
      inflation: 'Inflation assumption (%)',
      inflationHelp: 'Used to convert retirement growth assumptions into real spending power.',
      withdrawalStyle: 'Withdrawal mode',
      withdrawalStyleHelp: 'Controls whether your portfolio is allowed to shrink, stay level, or keep growing.',
      preserveCapital: 'Preserve capital',
      drawdown: 'Portfolio can shrink',
      keepGrowing: 'Portfolio should keep growing',
      targetEndBalance: 'Target portfolio balance at plan end',
      targetEndBalanceHelp: 'Only used for preserve/growth modes. Hidden when portfolio is allowed to shrink.',

      optionalFeasibility: 'Evaluate goal feasibility (optional)',
      optionalFeasibilitySubtitle:
        'Add budget numbers to test how realistic the required monthly contribution is for your current cash flow.',
      annualIncome: 'Your annual gross income',
      annualIncomeHelp: 'Used only for feasibility analysis.',
      useHouseholdIncome: 'Use household income for feasibility',
      householdIncome: 'Household annual gross income',
      householdIncomeHelp: 'If selected, feasibility uses this amount instead of your personal income when higher.',
      monthlyExpenses: 'Total monthly expenses',
      monthlyExpensesHelp: 'Include rent/mortgage, utilities, food, transport, etc.',
      monthlyDebtPayments: 'Monthly debt payments',
      monthlyDebtPaymentsHelp: 'Loans, credit cards, lines of credit, and similar fixed payments.',

      actionsTitle: 'Scenario actions',
      runModel: 'Estimate retirement plan',
      runningModel: 'Estimating...',
      saveModel: 'Save retirement model',
      savingModel: 'Saving...',
      saveSuccess: 'Retirement model saved to your planning history.',
      saveLogin: 'Sign in to save retirement models.',
      apiError: 'Live retirement model is unavailable. Showing local estimate.',

      resultsTitle: 'Retirement results',
      resultsSubtitle: 'Clear output first: how much you need, what your current path may deliver, and your feasibility check.',
      resultsLocked: 'Run the estimate to display retirement results.',

      requiredNestEgg: 'Required portfolio at retirement',
      requiredMonthlyContribution: 'Required monthly contribution from today',
      yearsToRetirement: 'Years until retirement',
      retirementYears: 'Years covered in retirement plan',
      targetEndBalanceResult: 'Target portfolio at plan end',

      projectedPortfolio: 'Projected portfolio at retirement (current contribution)',
      estimatedRange: 'Estimated monthly retirement income range',
      gap: 'Monthly surplus or shortfall vs target',
      projectedEndBalance: 'Projected portfolio at plan end (current path)',

      incomeBreakdownTitle: 'Income breakdown at retirement',
      govBenefits: 'Government benefits',
      portfolioTargetIncome: 'Portfolio income needed to hit target',
      drawLow: 'Portfolio withdrawal proxy (3%)',
      drawHigh: 'Portfolio withdrawal proxy (5%)',

      feasibilityTitle: 'Feasibility analysis',
      feasibilitySubtitle: 'Can your current budget realistically support the required monthly contribution?',
      disposableIncome: 'Estimated monthly disposable income',
      affordabilityRatio: 'Affordability ratio (disposable / required)',
      feasibilityStatus: 'Feasibility status',
      feasibilityStrong: 'Strongly feasible with current budget profile',
      feasibilityTight: 'Possible but tight - may need tradeoffs',
      feasibilityWeak: 'Currently not feasible without changes',
      feasibilityAdviceStrong: 'Your current budget appears capable of supporting the required contribution.',
      feasibilityAdviceTight: 'You are close. Small spending cuts or contribution increases over time can improve success odds.',
      feasibilityAdviceWeak: 'Consider a later retirement age, lower income target, or a staged contribution plan.',
    },
  },
  fr: {
    title: 'Assistant de planification de retraite',
    subtitle:
      'Repondez aux questions dans l ordre. Ensuite, voyez clairement le capital requis et la cotisation mensuelle necessaire.',
    labels: {
      profileTitle: 'Etape 1 : Profil de retraite',
      profileSubtitle: 'Definissez le calendrier et le revenu mensuel cible en dollars d aujourd hui.',
      currentAge: 'Age actuel',
      currentAgeHelp: 'Votre age aujourd hui.',
      retirementAge: 'Age de retraite',
      retirementAgeHelp: 'Age prevu pour cesser le travail a temps plein.',
      planEndAge: 'Age de fin du plan',
      planEndAgeHelp: 'Age jusqu auquel le plan de revenu de retraite doit tenir.',
      monthlyNeed: 'Revenu mensuel cible a la retraite (dollars d aujourd hui)',
      monthlyNeedHelp: 'Montant mensuel total souhaite a la retraite, avant la repartition par source.',
      monthlyContribution: 'Cotisation mensuelle actuelle pour la retraite',
      monthlyContributionHelp: 'Ce que vous epargnez actuellement chaque mois pour la retraite.',

      benefitsTitle: 'Etape 2 : Prestations gouvernementales (RPC, SV, SRG)',
      benefitsSubtitle: 'Incluez seulement les montants mensuels prevus a la retraite.',
      includeBenefits: 'Inclure RPC/SV/SRG dans le plan de revenu',
      cpp: 'Montant mensuel RPC',
      cppHelp: 'RPC = Regime de pensions du Canada. Estimation dans Mon dossier Service Canada.',
      oas: 'Montant mensuel SV',
      oasHelp: 'SV = Securite de la vieillesse. Le montant depend de l age et de la residence.',
      gis: 'Montant mensuel SRG',
      gisHelp: 'SRG = Supplement de revenu garanti pour les beneficiaires admissibles de la SV.',
      cppLinkLabel: 'Estimation RPC et releve',
      oasLinkLabel: 'Apercu du programme SV',
      gisLinkLabel: 'Apercu du programme SRG',

      assetsTitle: 'Etape 3 : Actifs retraite actuels',
      assetsSubtitle: 'Soldes disponibles pour la planification de revenu de retraite a long terme.',
      rrsp: 'Solde REER',
      rrspHelp: 'Valeur totale REER aujourd hui.',
      tfsa: 'Solde CELI',
      tfsaHelp: 'Valeur totale CELI disponible pour la retraite.',
      nonReg: 'Solde non enregistre',
      nonRegHelp: 'Valeur du compte imposable disponible pour la retraite.',

      assumptionsTitle: 'Etape 4 : Rendements et retraits',
      assumptionsSubtitle: 'Choisissez le comportement du portefeuille avant et pendant la retraite.',
      annualReturnPre: 'Rendement annuel avant retraite (%)',
      annualReturnPreHelp: 'Croissance annuelle moyenne attendue pendant la phase de cotisation.',
      annualReturnPost: 'Rendement annuel pendant retraite (%)',
      annualReturnPostHelp: 'Croissance annuelle moyenne attendue pendant les retraits.',
      inflation: 'Hypothese d inflation (%)',
      inflationHelp: 'Utilisee pour exprimer les hypotheses en pouvoir d achat reel.',
      withdrawalStyle: 'Mode de retrait',
      withdrawalStyleHelp: 'Determine si le portefeuille peut diminuer, rester stable, ou continuer de croitre.',
      preserveCapital: 'Preserver le capital',
      drawdown: 'Le portefeuille peut diminuer',
      keepGrowing: 'Le portefeuille doit continuer de croitre',
      targetEndBalance: 'Solde cible du portefeuille a la fin du plan',
      targetEndBalanceHelp: 'Utilise seulement en mode preservation/croissance. Cache si diminution permise.',

      optionalFeasibility: 'Evaluer la faisabilite (optionnel)',
      optionalFeasibilitySubtitle:
        'Ajoutez vos donnees budgetaires pour tester le realisme de la cotisation mensuelle requise.',
      annualIncome: 'Votre revenu brut annuel',
      annualIncomeHelp: 'Utilise seulement pour l analyse de faisabilite.',
      useHouseholdIncome: 'Utiliser le revenu du menage pour la faisabilite',
      householdIncome: 'Revenu brut annuel du menage',
      householdIncomeHelp: 'Si active, le calcul utilise ce montant au lieu du revenu personnel lorsqu il est plus eleve.',
      monthlyExpenses: 'Depenses mensuelles totales',
      monthlyExpensesHelp: 'Incluez logement, services, alimentation, transport, etc.',
      monthlyDebtPayments: 'Paiements mensuels de dettes',
      monthlyDebtPaymentsHelp: 'Prets, cartes de credit, marges de credit et paiements similaires.',

      actionsTitle: 'Actions de scenario',
      runModel: 'Estimer le plan de retraite',
      runningModel: 'Estimation...',
      saveModel: 'Enregistrer le modele retraite',
      savingModel: 'Enregistrement...',
      saveSuccess: 'Modele de retraite enregistre dans votre historique.',
      saveLogin: 'Connectez-vous pour enregistrer vos modeles retraite.',
      apiError: 'Le modele retraite en ligne est indisponible. Estimation locale affichee.',

      resultsTitle: 'Resultats retraite',
      resultsSubtitle: 'Sortie claire : besoins, projection du parcours actuel et verification de faisabilite.',
      resultsLocked: 'Lancez l estimation pour afficher les resultats retraite.',

      requiredNestEgg: 'Portefeuille requis a la retraite',
      requiredMonthlyContribution: 'Cotisation mensuelle requise a partir d aujourd hui',
      yearsToRetirement: 'Annees avant la retraite',
      retirementYears: 'Annees couvertes par le plan retraite',
      targetEndBalanceResult: 'Portefeuille cible a la fin du plan',

      projectedPortfolio: 'Portefeuille projete a la retraite (cotisation actuelle)',
      estimatedRange: 'Intervalle estime de revenu mensuel retraite',
      gap: 'Surplus ou manque mensuel vs cible',
      projectedEndBalance: 'Portefeuille projete en fin de plan (parcours actuel)',

      incomeBreakdownTitle: 'Repartition du revenu a la retraite',
      govBenefits: 'Prestations gouvernementales',
      portfolioTargetIncome: 'Revenu necessaire du portefeuille pour atteindre la cible',
      drawLow: 'Retrait theorique du portefeuille (3%)',
      drawHigh: 'Retrait theorique du portefeuille (5%)',

      feasibilityTitle: 'Analyse de faisabilite',
      feasibilitySubtitle: 'Votre budget actuel peut-il soutenir la cotisation mensuelle requise?',
      disposableIncome: 'Revenu disponible mensuel estime',
      affordabilityRatio: 'Ratio de capacite (disponible / requis)',
      feasibilityStatus: 'Statut de faisabilite',
      feasibilityStrong: 'Objectif fortement realisable avec votre budget actuel',
      feasibilityTight: 'Objectif possible, mais serre',
      feasibilityWeak: 'Objectif peu realisable sans changements',
      feasibilityAdviceStrong: 'Votre budget actuel semble capable de soutenir la cotisation requise.',
      feasibilityAdviceTight: 'Vous etes proche. De petits ajustements peuvent augmenter la probabilite de reussite.',
      feasibilityAdviceWeak: 'Considerez un age de retraite plus tardif, une cible plus basse, ou une progression de cotisation.',
    },
  },
};

const formatMoney = (value, lang) =>
  new Intl.NumberFormat(lang === 'fr' ? 'fr-CA' : 'en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(Number.isFinite(value) ? value : 0);

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const toNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const requiredCapitalForRetirementIncome = (annualIncome, annualRealReturn, yearsInRetirement, targetEndBalance) => {
  if (yearsInRetirement <= 0) return targetEndBalance;
  if (Math.abs(annualRealReturn) < 0.000001) {
    return annualIncome * yearsInRetirement + targetEndBalance;
  }

  const discount = Math.pow(1 + annualRealReturn, -yearsInRetirement);
  const annuityPv = annualIncome * ((1 - discount) / annualRealReturn);
  const terminalPv = targetEndBalance * discount;
  return Math.max(0, annuityPv + terminalPv);
};

const requiredMonthlyContributionForTarget = (targetFutureValue, currentAssets, annualReturn, yearsToRetirement) => {
  const months = Math.max(0, Math.round(yearsToRetirement * 12));
  if (months === 0) return 0;

  const monthlyRate = annualReturn / 12;
  const futureAssets = currentAssets * Math.pow(1 + monthlyRate, months);
  const remaining = Math.max(0, targetFutureValue - futureAssets);
  if (remaining <= 0) return 0;

  if (Math.abs(monthlyRate) < 0.000001) return remaining / months;

  const annuityFactor = (Math.pow(1 + monthlyRate, months) - 1) / monthlyRate;
  return remaining / annuityFactor;
};

const projectPortfolioAtRetirement = (assetsNow, monthlyContribution, annualReturn, yearsToRetirement) => {
  const months = Math.max(0, Math.round(yearsToRetirement * 12));
  if (months === 0) return assetsNow;

  const monthlyRate = annualReturn / 12;
  const grownAssets = assetsNow * Math.pow(1 + monthlyRate, months);
  const contributed = Math.abs(monthlyRate) < 0.000001
    ? monthlyContribution * months
    : monthlyContribution * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate);
  return Math.max(0, grownAssets + contributed);
};

const buildFeasibility = ({ enabled, income, householdIncome, useHouseholdIncome, monthlyExpenses, monthlyDebtPayments, requiredMonthlyContribution }, c) => {
  if (!enabled) return null;

  const annualIncome = useHouseholdIncome
    ? Math.max(toNumber(householdIncome, 0), toNumber(income, 0))
    : toNumber(income, 0);

  const disposableMonthly = Math.max(
    0,
    annualIncome / 12 - Math.max(0, toNumber(monthlyExpenses, 0)) - Math.max(0, toNumber(monthlyDebtPayments, 0))
  );

  const required = Math.max(1, toNumber(requiredMonthlyContribution, 0));
  const ratio = disposableMonthly / required;

  if (ratio >= 1.25) {
    return {
      disposableMonthly,
      ratio,
      status: c.labels.feasibilityStrong,
      advice: c.labels.feasibilityAdviceStrong,
    };
  }

  if (ratio >= 0.9) {
    return {
      disposableMonthly,
      ratio,
      status: c.labels.feasibilityTight,
      advice: c.labels.feasibilityAdviceTight,
    };
  }

  return {
    disposableMonthly,
    ratio,
    status: c.labels.feasibilityWeak,
    advice: c.labels.feasibilityAdviceWeak,
  };
};

const RetirementReadinessCenter = ({ apiBase, authUser }) => {
  const { lang } = useLanguage();
  const c = COPY[lang] || COPY.en;

  const [input, setInput] = useState({
    currentAge: 38,
    retirementAge: 62,
    planEndAge: 92,
    monthlyNeed: 5500,
    monthlyContribution: 900,

    includeBenefits: 1,
    cpp: 900,
    oas: 760,
    gis: 0,

    rrsp: 140000,
    tfsa: 50000,
    nonReg: 25000,

    annualReturnPre: 5.5,
    annualReturnPost: 4.2,
    inflation: 2,
    withdrawalStyle: 'drawdown',
    targetEndBalance: 300000,

    enableFeasibility: 0,
    income: 95000,
    householdIncome: 130000,
    useHouseholdIncome: 1,
    monthlyExpenses: 5200,
    monthlyDebtPayments: 900,
  });

  const [remoteModel, setRemoteModel] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [saveMessage, setSaveMessage] = useState('');

  const localModel = useMemo(() => {
    const currentAge = clamp(toNumber(input.currentAge, 38), 18, 95);
    const retirementAge = clamp(toNumber(input.retirementAge, 62), currentAge, 95);
    const planEndAge = clamp(toNumber(input.planEndAge, 92), retirementAge + 1, 110);

    const yearsToRetirement = retirementAge - currentAge;
    const retirementYears = Math.max(1, planEndAge - retirementAge);

    const annualReturnPre = clamp(toNumber(input.annualReturnPre, 5.5) / 100, -0.02, 0.12);
    const annualReturnPost = clamp(toNumber(input.annualReturnPost, 4.2) / 100, -0.02, 0.12);
    const inflation = clamp(toNumber(input.inflation, 2) / 100, 0, 0.08);

    const assetsNow = Math.max(0, toNumber(input.rrsp, 0)) + Math.max(0, toNumber(input.tfsa, 0)) + Math.max(0, toNumber(input.nonReg, 0));

    const governmentBenefits = Boolean(input.includeBenefits)
      ? Math.max(0, toNumber(input.cpp, 0)) + Math.max(0, toNumber(input.oas, 0)) + Math.max(0, toNumber(input.gis, 0))
      : 0;

    const targetMonthlyIncome = Math.max(0, toNumber(input.monthlyNeed, 0));
    const portfolioIncomeTarget = Math.max(0, targetMonthlyIncome - governmentBenefits);

    const resolvedTargetEndBalance = input.withdrawalStyle === 'drawdown'
      ? 0
      : input.withdrawalStyle === 'preserve'
        ? Math.max(assetsNow, toNumber(input.targetEndBalance, assetsNow))
        : Math.max(assetsNow * 1.15, toNumber(input.targetEndBalance, assetsNow * 1.15));

    const annualRealReturnPost = ((1 + annualReturnPost) / (1 + inflation)) - 1;

    const requiredNestEgg = requiredCapitalForRetirementIncome(
      portfolioIncomeTarget * 12,
      annualRealReturnPost,
      retirementYears,
      resolvedTargetEndBalance
    );

    const requiredMonthlyContribution = requiredMonthlyContributionForTarget(
      requiredNestEgg,
      assetsNow,
      annualReturnPre,
      yearsToRetirement
    );

    const projectedPortfolio = projectPortfolioAtRetirement(
      assetsNow,
      Math.max(0, toNumber(input.monthlyContribution, 0)),
      annualReturnPre,
      yearsToRetirement
    );

    const drawLow = (projectedPortfolio * 0.03) / 12;
    const drawHigh = (projectedPortfolio * 0.05) / 12;
    const estimatedLow = governmentBenefits + drawLow;
    const estimatedHigh = governmentBenefits + drawHigh;
    const estimatedMid = (estimatedLow + estimatedHigh) / 2;
    const gap = estimatedMid - targetMonthlyIncome;

    const projectedEndBalance = Math.max(
      0,
      requiredCapitalForRetirementIncome(
        portfolioIncomeTarget * 12,
        annualRealReturnPost,
        retirementYears,
        0
      ) <= projectedPortfolio
        ? resolvedTargetEndBalance
        : projectedPortfolio - Math.max(0, (portfolioIncomeTarget * 12) - (projectedPortfolio * annualRealReturnPost)) * retirementYears
    );

    const feasibility = buildFeasibility(
      {
        enabled: Boolean(input.enableFeasibility),
        income: input.income,
        householdIncome: input.householdIncome,
        useHouseholdIncome: Boolean(input.useHouseholdIncome),
        monthlyExpenses: input.monthlyExpenses,
        monthlyDebtPayments: input.monthlyDebtPayments,
        requiredMonthlyContribution,
      },
      c
    );

    return {
      yearsToRetirement,
      retirementYears,
      requiredNestEgg,
      requiredMonthlyContribution,
      targetEndBalance: resolvedTargetEndBalance,
      projectedPortfolio,
      estimatedLow,
      estimatedHigh,
      gap,
      projectedEndBalance,
      governmentBenefits,
      portfolioIncomeTarget,
      drawLow,
      drawHigh,
      feasibility,
    };
  }, [input, c]);

  const model = remoteModel || localModel;

  const update = (key, value) => {
    setRemoteModel(null);
    setErrorMessage('');
    setSaveMessage('');

    if (key === 'withdrawalStyle') {
      setInput((prev) => ({ ...prev, [key]: value }));
      return;
    }

    if (key === 'includeBenefits' || key === 'enableFeasibility' || key === 'useHouseholdIncome') {
      setInput((prev) => ({ ...prev, [key]: value ? 1 : 0 }));
      return;
    }

    setInput((prev) => ({ ...prev, [key]: Number(value) }));
  };

  const buildApiPayload = (saveModel = false) => ({
    current_age: Number(input.currentAge),
    retirement_age: Number(input.retirementAge),
    life_expectancy: Number(input.planEndAge),
    target_monthly_income: Number(input.monthlyNeed),
    monthly_contribution: Number(input.monthlyContribution),
    estimated_cpp: Number(input.cpp),
    estimated_oas: Number(input.oas),
    estimated_gis: Number(input.gis),
    rrsp_balance: Number(input.rrsp),
    tfsa_balance: Number(input.tfsa),
    non_registered_balance: Number(input.nonReg),
    annual_return_pct: Number(input.annualReturnPre),
    annual_return_pre_pct: Number(input.annualReturnPre),
    annual_return_post_pct: Number(input.annualReturnPost),
    inflation_pct: Number(input.inflation),
    include_benefits: Boolean(input.includeBenefits),
    withdrawal_style: input.withdrawalStyle,
    end_balance_target: Number(input.withdrawalStyle === 'drawdown' ? 0 : input.targetEndBalance),
    save_model: saveModel,
  });

  const runRetirementModel = async (saveModel = false) => {
    setShowResults(true);

    if (!apiBase) {
      setRemoteModel(null);
      return;
    }

    if (saveModel && !authUser) {
      setSaveMessage(c.labels.saveLogin);
      return;
    }

    setErrorMessage('');
    setSaveMessage('');
    if (saveModel) setSaving(true);
    else setLoading(true);

    try {
      const response = await axios.post(`${apiBase}/planning/retirement`, buildApiPayload(saveModel), {
        withCredentials: true,
      });

      const apiModel = response?.data?.model;
      if (apiModel) {
        setRemoteModel({
          yearsToRetirement: Number(apiModel.years_to_retirement) || localModel.yearsToRetirement,
          retirementYears: Number(apiModel.years_in_retirement) || localModel.retirementYears,
          requiredNestEgg: Number(apiModel.required_nest_egg) || localModel.requiredNestEgg,
          requiredMonthlyContribution: Number(apiModel.required_monthly_contribution) || localModel.requiredMonthlyContribution,
          targetEndBalance: Number(apiModel.target_end_balance) || localModel.targetEndBalance,
          projectedPortfolio: Number(apiModel.projected_portfolio) || localModel.projectedPortfolio,
          estimatedLow: Number(apiModel.estimated_low) || localModel.estimatedLow,
          estimatedHigh: Number(apiModel.estimated_high) || localModel.estimatedHigh,
          gap: Number(apiModel.gap) || localModel.gap,
          projectedEndBalance: Number(apiModel.projected_end_balance) || localModel.projectedEndBalance,
          governmentBenefits: Number(apiModel.gov_income) || localModel.governmentBenefits,
          portfolioIncomeTarget: Number(apiModel.portfolio_income_target) || localModel.portfolioIncomeTarget,
          drawLow: Number(apiModel.draw_low) || localModel.drawLow,
          drawHigh: Number(apiModel.draw_high) || localModel.drawHigh,
          feasibility: localModel.feasibility,
        });
      }

      if (response?.data?.saved_retirement_model_id) {
        setSaveMessage(c.labels.saveSuccess);
      }
    } catch (_error) {
      setRemoteModel(null);
      setErrorMessage(c.labels.apiError);
    } finally {
      setLoading(false);
      setSaving(false);
    }
  };

  return (
    <div className="retirement-root">
      <section className="retirement-hero dashboard-glass">
        <h2>{c.title}</h2>
        <p>{c.subtitle}</p>
      </section>

      <ComplianceNotice compact />

      <section className="retirement-layout">
        <article className="retirement-form dashboard-glass">
          <section className="retirement-section">
            <h3>{c.labels.profileTitle}</h3>
            <p>{c.labels.profileSubtitle}</p>
            <div className="retirement-form-grid">
              <label>
                <span>{c.labels.currentAge}</span>
                <input type="number" value={input.currentAge} onChange={(e) => update('currentAge', e.target.value)} min="18" max="95" />
                <small>{c.labels.currentAgeHelp}</small>
              </label>
              <label>
                <span>{c.labels.retirementAge}</span>
                <input type="number" value={input.retirementAge} onChange={(e) => update('retirementAge', e.target.value)} min="40" max="95" />
                <small>{c.labels.retirementAgeHelp}</small>
              </label>
              <label>
                <span>{c.labels.planEndAge}</span>
                <input type="number" value={input.planEndAge} onChange={(e) => update('planEndAge', e.target.value)} min="65" max="110" />
                <small>{c.labels.planEndAgeHelp}</small>
              </label>
              <label>
                <span>{c.labels.monthlyNeed}</span>
                <input type="number" value={input.monthlyNeed} onChange={(e) => update('monthlyNeed', e.target.value)} min="0" step="100" />
                <small>{c.labels.monthlyNeedHelp}</small>
              </label>
              <label>
                <span>{c.labels.monthlyContribution}</span>
                <input type="number" value={input.monthlyContribution} onChange={(e) => update('monthlyContribution', e.target.value)} min="0" step="50" />
                <small>{c.labels.monthlyContributionHelp}</small>
              </label>
            </div>
          </section>

          <section className="retirement-section">
            <h3>{c.labels.benefitsTitle}</h3>
            <p>{c.labels.benefitsSubtitle}</p>

            <label className="retirement-toggle-row">
              <input type="checkbox" checked={Boolean(input.includeBenefits)} onChange={(e) => update('includeBenefits', e.target.checked)} />
              <span>{c.labels.includeBenefits}</span>
            </label>

            <div className="retirement-form-grid">
              <label>
                <span>{c.labels.cpp}</span>
                <input type="number" value={input.cpp} onChange={(e) => update('cpp', e.target.value)} min="0" step="10" />
                <small>{c.labels.cppHelp}</small>
              </label>
              <label>
                <span>{c.labels.oas}</span>
                <input type="number" value={input.oas} onChange={(e) => update('oas', e.target.value)} min="0" step="10" />
                <small>{c.labels.oasHelp}</small>
              </label>
              <label>
                <span>{c.labels.gis}</span>
                <input type="number" value={input.gis} onChange={(e) => update('gis', e.target.value)} min="0" step="10" />
                <small>{c.labels.gisHelp}</small>
              </label>
            </div>

            <div className="retirement-links">
              <a href="https://www.canada.ca/en/services/benefits/publicpensions/cpp.html" target="_blank" rel="noreferrer">{c.labels.cppLinkLabel}</a>
              <a href="https://www.canada.ca/en/services/benefits/publicpensions/cpp/old-age-security.html" target="_blank" rel="noreferrer">{c.labels.oasLinkLabel}</a>
              <a href="https://www.canada.ca/en/services/benefits/publicpensions/cpp/old-age-security/guaranteed-income-supplement.html" target="_blank" rel="noreferrer">{c.labels.gisLinkLabel}</a>
            </div>
          </section>

          <section className="retirement-section">
            <h3>{c.labels.assetsTitle}</h3>
            <p>{c.labels.assetsSubtitle}</p>
            <div className="retirement-form-grid">
              <label>
                <span>{c.labels.rrsp}</span>
                <input type="number" value={input.rrsp} onChange={(e) => update('rrsp', e.target.value)} min="0" step="1000" />
                <small>{c.labels.rrspHelp}</small>
              </label>
              <label>
                <span>{c.labels.tfsa}</span>
                <input type="number" value={input.tfsa} onChange={(e) => update('tfsa', e.target.value)} min="0" step="1000" />
                <small>{c.labels.tfsaHelp}</small>
              </label>
              <label>
                <span>{c.labels.nonReg}</span>
                <input type="number" value={input.nonReg} onChange={(e) => update('nonReg', e.target.value)} min="0" step="1000" />
                <small>{c.labels.nonRegHelp}</small>
              </label>
            </div>
          </section>

          <section className="retirement-section">
            <h3>{c.labels.assumptionsTitle}</h3>
            <p>{c.labels.assumptionsSubtitle}</p>
            <div className="retirement-form-grid">
              <label>
                <span>{c.labels.annualReturnPre}</span>
                <input type="number" value={input.annualReturnPre} onChange={(e) => update('annualReturnPre', e.target.value)} min="0" max="12" step="0.1" />
                <small>{c.labels.annualReturnPreHelp}</small>
              </label>
              <label>
                <span>{c.labels.annualReturnPost}</span>
                <input type="number" value={input.annualReturnPost} onChange={(e) => update('annualReturnPost', e.target.value)} min="0" max="10" step="0.1" />
                <small>{c.labels.annualReturnPostHelp}</small>
              </label>
              <label>
                <span>{c.labels.inflation}</span>
                <input type="number" value={input.inflation} onChange={(e) => update('inflation', e.target.value)} min="0" max="8" step="0.1" />
                <small>{c.labels.inflationHelp}</small>
              </label>
              <label>
                <span>{c.labels.withdrawalStyle}</span>
                <select value={input.withdrawalStyle} onChange={(e) => update('withdrawalStyle', e.target.value)}>
                  <option value="preserve">{c.labels.preserveCapital}</option>
                  <option value="drawdown">{c.labels.drawdown}</option>
                  <option value="grow">{c.labels.keepGrowing}</option>
                </select>
                <small>{c.labels.withdrawalStyleHelp}</small>
              </label>
              {input.withdrawalStyle !== 'drawdown' && (
                <label>
                  <span>{c.labels.targetEndBalance}</span>
                  <input type="number" value={input.targetEndBalance} onChange={(e) => update('targetEndBalance', e.target.value)} min="0" step="10000" />
                  <small>{c.labels.targetEndBalanceHelp}</small>
                </label>
              )}
            </div>
          </section>

          <section className="retirement-section optional">
            <label className="retirement-toggle-row">
              <input type="checkbox" checked={Boolean(input.enableFeasibility)} onChange={(e) => update('enableFeasibility', e.target.checked)} />
              <span>{c.labels.optionalFeasibility}</span>
            </label>
            <p>{c.labels.optionalFeasibilitySubtitle}</p>

            {Boolean(input.enableFeasibility) && (
              <div className="retirement-form-grid">
                <label>
                  <span>{c.labels.annualIncome}</span>
                  <input type="number" value={input.income} onChange={(e) => update('income', e.target.value)} min="0" step="1000" />
                  <small>{c.labels.annualIncomeHelp}</small>
                </label>
                <label className="retirement-toggle-field">
                  <span>{c.labels.useHouseholdIncome}</span>
                  <input type="checkbox" checked={Boolean(input.useHouseholdIncome)} onChange={(e) => update('useHouseholdIncome', e.target.checked)} />
                </label>
                <label>
                  <span>{c.labels.householdIncome}</span>
                  <input type="number" value={input.householdIncome} onChange={(e) => update('householdIncome', e.target.value)} min="0" step="1000" />
                  <small>{c.labels.householdIncomeHelp}</small>
                </label>
                <label>
                  <span>{c.labels.monthlyExpenses}</span>
                  <input type="number" value={input.monthlyExpenses} onChange={(e) => update('monthlyExpenses', e.target.value)} min="0" step="100" />
                  <small>{c.labels.monthlyExpensesHelp}</small>
                </label>
                <label>
                  <span>{c.labels.monthlyDebtPayments}</span>
                  <input type="number" value={input.monthlyDebtPayments} onChange={(e) => update('monthlyDebtPayments', e.target.value)} min="0" step="50" />
                  <small>{c.labels.monthlyDebtPaymentsHelp}</small>
                </label>
              </div>
            )}
          </section>

          <div className="retirement-actions">
            <span>{c.labels.actionsTitle}</span>
            <button type="button" onClick={() => runRetirementModel(false)} disabled={loading}>
              {loading ? c.labels.runningModel : c.labels.runModel}
            </button>
            <button
              type="button"
              onClick={() => runRetirementModel(true)}
              disabled={saving || !authUser}
              title={!authUser ? c.labels.saveLogin : c.labels.saveModel}
            >
              {saving ? c.labels.savingModel : c.labels.saveModel}
            </button>
          </div>

          {errorMessage && <p className="retirement-feedback error">{errorMessage}</p>}
          {saveMessage && <p className="retirement-feedback success">{saveMessage}</p>}
        </article>

        <article className="retirement-results dashboard-glass">
          <header className="retirement-results-head">
            <h3>{c.labels.resultsTitle}</h3>
            <p>{c.labels.resultsSubtitle}</p>
          </header>

          {!showResults && <p className="retirement-results-locked">{c.labels.resultsLocked}</p>}

          {showResults && (
            <>
              <section className="retirement-result-group">
                <h4>{lang === 'fr' ? 'Ce que votre plan exige' : 'What your plan requires'}</h4>
                <div className="retirement-kpi-grid">
                  <div><span>{c.labels.requiredNestEgg}</span><strong>{formatMoney(model.requiredNestEgg, lang)}</strong></div>
                  <div><span>{c.labels.requiredMonthlyContribution}</span><strong>{formatMoney(model.requiredMonthlyContribution, lang)}</strong></div>
                  <div><span>{c.labels.yearsToRetirement}</span><strong>{model.yearsToRetirement}</strong></div>
                  <div><span>{c.labels.retirementYears}</span><strong>{model.retirementYears}</strong></div>
                  {input.withdrawalStyle !== 'drawdown' && (
                    <div><span>{c.labels.targetEndBalanceResult}</span><strong>{formatMoney(model.targetEndBalance, lang)}</strong></div>
                  )}
                </div>
              </section>

              <section className="retirement-result-group">
                <h4>{lang === 'fr' ? 'Ce que votre parcours actuel peut produire' : 'What your current path may produce'}</h4>
                <div className="retirement-kpi-grid">
                  <div><span>{c.labels.projectedPortfolio}</span><strong>{formatMoney(model.projectedPortfolio, lang)}</strong></div>
                  <div><span>{c.labels.estimatedRange}</span><strong>{formatMoney(model.estimatedLow, lang)} - {formatMoney(model.estimatedHigh, lang)}</strong></div>
                  <div>
                    <span>{c.labels.gap}</span>
                    <strong className={model.gap >= 0 ? 'positive' : 'negative'}>{formatMoney(model.gap, lang)}</strong>
                  </div>
                  <div><span>{c.labels.projectedEndBalance}</span><strong>{formatMoney(model.projectedEndBalance, lang)}</strong></div>
                </div>
              </section>

              <section className="retirement-result-group">
                <h4>{c.labels.incomeBreakdownTitle}</h4>
                <ul className="retirement-list-panel">
                  <li><span>{c.labels.govBenefits}</span><strong>{formatMoney(model.governmentBenefits, lang)}</strong></li>
                  <li><span>{c.labels.portfolioTargetIncome}</span><strong>{formatMoney(model.portfolioIncomeTarget, lang)}</strong></li>
                  <li><span>{c.labels.drawLow}</span><strong>{formatMoney(model.drawLow, lang)}</strong></li>
                  <li><span>{c.labels.drawHigh}</span><strong>{formatMoney(model.drawHigh, lang)}</strong></li>
                </ul>
              </section>

              {model.feasibility && (
                <section className="retirement-result-group feasibility">
                  <h4>{c.labels.feasibilityTitle}</h4>
                  <p>{c.labels.feasibilitySubtitle}</p>
                  <ul className="retirement-list-panel">
                    <li><span>{c.labels.disposableIncome}</span><strong>{formatMoney(model.feasibility.disposableMonthly, lang)}</strong></li>
                    <li><span>{c.labels.affordabilityRatio}</span><strong>{model.feasibility.ratio.toFixed(2)}x</strong></li>
                    <li><span>{c.labels.feasibilityStatus}</span><strong>{model.feasibility.status}</strong></li>
                  </ul>
                  <p className="retirement-feasibility-advice">{model.feasibility.advice}</p>
                </section>
              )}
            </>
          )}
        </article>
      </section>
    </div>
  );
};

export default RetirementReadinessCenter;
